import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ApiEndpoint } from '@constants/index';
import { PRIVATE_PAGES } from '@app/router/pages';
import { useCreateImageMutation } from '@modules/images';
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useLazyGetCategoryByIdQuery,
} from '@modules/category';
import { ICategoryImage } from '@modules/category/types';

import { TitlesWrapper, ImagePreviewBox } from './styled';

const CategoryMutation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [titleAm, setTitleAm] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [titleRu, setTitleRu] = useState('');

  const [descriptionAm, setDescriptionAm] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionRu, setDescriptionRu] = useState('');

  const [existingImages, setExistingImages] = useState<ICategoryImage[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [visible, setVisible] = useState(true);
  const [visibleOnMap, setVisibleOnMap] = useState(true);
  const [titleError, setTitleError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState<{ text: string; isError?: boolean } | null>(null);

  const [getCategoryById, { isLoading: isFetchingCategory }] = useLazyGetCategoryByIdQuery();
  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [createImage] = useCreateImageMutation();

  useEffect(() => {
    if (id) {
      getCategoryById(id)
        .unwrap()
        .then((cat) => {
          if (cat) {
            setTitleAm(cat.titleAm || '');
            setTitleEn(cat.titleEn || cat.title || '');
            setTitleRu(cat.titleRu || '');

            setDescriptionAm(cat.descriptionAm || '');
            setDescriptionEn(cat.descriptionEn || cat.description || '');
            setDescriptionRu(cat.descriptionRu || '');

            setExistingImages(cat.images || []);
            setVisible(cat.visible !== undefined ? Boolean(cat.visible) : true);
            setVisibleOnMap(cat.visibleOnMap !== undefined ? Boolean(cat.visibleOnMap) : true);
          }
        })
        .catch((err) => {
          console.error(err);
          setServerMessage({ text: 'Failed to load category', isError: true });
        });
    }
  }, [id, getCategoryById]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedFile(file);

      const objectUrl = URL.createObjectURL(file);

      setPreviewUrl(objectUrl);
    }
  };

  const handleRemoveSelectedFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleRemoveExistingImage = (imageId: string) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titleAm.trim() && !titleEn.trim() && !titleRu.trim()) {
      setTitleError('At least one title (Am, En, or Ru) is required');

      return;
    }

    setTitleError('');
    setIsSubmitting(true);
    setServerMessage(null);

    try {
      let imageIds: string[] = existingImages.map((img) => img.id);

      if (selectedFile) {
        const formData = new FormData();

        formData.append('files[]', selectedFile);

        const imageRes = await createImage(formData).unwrap();

        if (imageRes && (imageRes as any).id) {
          imageIds = [(imageRes as any).id];
        }
      }

      const primaryTitle = (titleEn || titleAm || titleRu).trim();
      const primaryDescription = (descriptionEn || descriptionAm || descriptionRu).trim();

      const payload = {
        title: primaryTitle,
        titleAm: titleAm.trim(),
        titleEn: titleEn.trim(),
        titleRu: titleRu.trim(),
        description: primaryDescription,
        descriptionAm: descriptionAm.trim(),
        descriptionEn: descriptionEn.trim(),
        descriptionRu: descriptionRu.trim(),
        images: imageIds,
        visible,
        visibleOnMap,
      };

      if (id) {
        await updateCategory({ id, body: payload }).unwrap();
        setServerMessage({ text: 'Category updated successfully!' });
      } else {
        await createCategory(payload).unwrap();
        setServerMessage({ text: 'Category created successfully!' });
      }

      setTimeout(() => {
        navigate(PRIVATE_PAGES.CATEGORIES);
      }, 1000);
    } catch (error: any) {
      console.error(error);
      setServerMessage({
        text: error?.data?.message || 'Something went wrong',
        isError: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetchingCategory) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack component="form" onSubmit={handleSubmit} sx={{ maxWidth: '700px' }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        {id ? 'Edit Category' : 'Create New Category'}
      </Typography>

      <TitlesWrapper>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Titles (Multilingual)
        </Typography>

        <TextField
          label="Title Am"
          value={titleAm}
          onChange={(e) => {
            setTitleAm(e.target.value);
            if (e.target.value.trim()) {
              setTitleError('');
            }
          }}
          error={!!titleError && !titleAm && !titleEn && !titleRu}
          helperText={titleError && !titleAm && !titleEn && !titleRu ? titleError : ''}
          fullWidth
        />

        <TextField
          label="Title En"
          value={titleEn}
          onChange={(e) => {
            setTitleEn(e.target.value);
            if (e.target.value.trim()) {
              setTitleError('');
            }
          }}
          fullWidth
        />

        <TextField
          label="Title Ru"
          value={titleRu}
          onChange={(e) => {
            setTitleRu(e.target.value);
            if (e.target.value.trim()) {
              setTitleError('');
            }
          }}
          fullWidth
        />

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
          Descriptions (Multilingual)
        </Typography>

        <TextField
          label="Description Am"
          value={descriptionAm}
          onChange={(e) => setDescriptionAm(e.target.value)}
          multiline
          rows={3}
          fullWidth
        />

        <TextField
          label="Description En"
          value={descriptionEn}
          onChange={(e) => setDescriptionEn(e.target.value)}
          multiline
          rows={3}
          fullWidth
        />

        <TextField
          label="Description Ru"
          value={descriptionRu}
          onChange={(e) => setDescriptionRu(e.target.value)}
          multiline
          rows={3}
          fullWidth
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Category Image
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {!previewUrl &&
              existingImages.map((img) => (
                <ImagePreviewBox key={img.id}>
                  <img src={`${ApiEndpoint}/${img.url}`} alt="Category" />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveExistingImage(img.id)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      '&:hover': { backgroundColor: 'white' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ImagePreviewBox>
              ))}

            {previewUrl && (
              <ImagePreviewBox>
                <img src={previewUrl} alt="Preview" />
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleRemoveSelectedFile}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.85)',
                    '&:hover': { backgroundColor: 'white' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ImagePreviewBox>
            )}

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              {previewUrl || existingImages.length > 0 ? 'Change Image' : 'Upload Image'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            Recommended: 400×400 px (1:1 square ratio, centered subject). Formats: WebP, PNG, JPG (max 5MB).
          </Typography>
        </Box>

        <Box sx={{ mt: 3, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Visibility in website (Products)
            </Typography>
            <RadioGroup
              row
              value={visible ? 'yes' : 'no'}
              onChange={(e) => setVisible(e.target.value === 'yes')}
            >
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
            </RadioGroup>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Visibility on map
            </Typography>
            <RadioGroup
              row
              value={visibleOnMap ? 'yes' : 'no'}
              onChange={(e) => setVisibleOnMap(e.target.value === 'yes')}
            >
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
            </RadioGroup>
          </Box>
        </Box>
      </TitlesWrapper>

      {serverMessage && (
        <Typography
          sx={{
            mb: 2,
            color: serverMessage.isError ? 'error.main' : 'success.main',
            fontWeight: 500,
          }}
        >
          {serverMessage.text}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : id ? (
            'Save Changes'
          ) : (
            'Create Category'
          )}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(PRIVATE_PAGES.CATEGORIES)}
        >
          Cancel
        </Button>
      </Box>
    </Stack>
  );
};

export default CategoryMutation;
